function slugifyHeading(text) {
  return text
    .toString()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/['’"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

if (!customElements.get('gl-article')) {
  customElements.define(
    'gl-article',
    class GlArticle extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector('[data-article-content]');
        this.toc = this.querySelector('[data-article-toc]');
        this.tocNav = this.querySelector('[data-article-toc-nav]');
        this.headings = [];
        this.desktopQuery = window.matchMedia('(min-width: 990px)');

        this.promoteBoldHeadings();
        this.wrapCaptions();
        this.initHeadings();
        this.initMedia();
        this.initShare();
        this.initLightbox();
        this.syncTocOpen();
        this.onDesktopChange = () => this.syncTocOpen();
        this.desktopQuery.addEventListener('change', this.onDesktopChange);
        this.initTocObserver();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onDesktopChange);
      }

      paragraphHeadingText(paragraph) {
        const clone = paragraph.cloneNode(true);
        clone.querySelectorAll('br, sup, .superscript').forEach((node) => node.remove());
        return clone.textContent.replace(/\s+/g, ' ').trim();
      }

      promoteBoldHeadings() {
        if (!this.content) return;

        Array.from(this.content.querySelectorAll('p')).forEach((paragraph) => {
          if (paragraph.closest('li, blockquote, figcaption')) return;
          if (paragraph.querySelector('img, video, iframe')) return;

          const text = this.paragraphHeadingText(paragraph);
          if (!text || text.length > 110) return;

          const bold = paragraph.querySelector('b, strong');
          if (!bold) return;

          const boldText = bold.textContent.replace(/\s+/g, ' ').trim();
          if (!boldText || boldText !== text) return;

          const heading = document.createElement('h2');
          heading.innerHTML = paragraph.innerHTML;
          paragraph.replaceWith(heading);
        });
      }

      captionFrom(node) {
        if (!node || node.tagName !== 'P') return '';
        if (node.querySelector('a, img, video, iframe, ul, ol')) return '';
        const text = node.textContent.replace(/\s+/g, ' ').trim();
        if (!text || text.length > 110) return '';
        return text;
      }

      attachFollowingCaption(figure) {
        if (figure.querySelector('figcaption')) return;
        const captionText = this.captionFrom(figure.nextElementSibling);
        if (!captionText) return;
        const caption = document.createElement('figcaption');
        caption.textContent = captionText;
        figure.appendChild(caption);
        figure.nextElementSibling.remove();
      }

      wrapCaptions() {
        if (!this.content) return;

        this.content.querySelectorAll('img').forEach((img) => {
          const existing = img.closest('figure');
          if (existing) {
            existing.classList.add('gl-article__figure');
            this.attachFollowingCaption(existing);
            return;
          }

          const parent = img.parentElement;
          if (!parent || parent.tagName !== 'P') return;

          const siblings = Array.from(parent.childNodes).filter((node) => {
            if (node === img) return false;
            return node.nodeType === 1 || (node.nodeType === 3 && node.textContent.trim());
          });
          const hasOtherMedia = siblings.some((node) => {
            if (node.nodeType !== 1) return false;
            return node.matches('img, video, iframe, a') || !!node.querySelector('img, video, iframe');
          });
          const captionText = siblings
            .map((node) => node.textContent)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();

          const figure = document.createElement('figure');
          figure.className = 'gl-article__figure';
          parent.insertBefore(figure, img);
          figure.appendChild(img);

          if (!hasOtherMedia && captionText && captionText.length <= 110) {
            const caption = document.createElement('figcaption');
            caption.textContent = captionText;
            figure.appendChild(caption);
            siblings.forEach((node) => node.remove());
          }

          const leftover = Array.from(parent.childNodes).filter((node) => {
            if (node === figure) return false;
            return node.nodeType === 1 || (node.nodeType === 3 && node.textContent.trim());
          });
          if (leftover.length === 0) parent.replaceWith(figure);
          else this.attachFollowingCaption(figure);
        });
      }

      initHeadings() {
        if (!this.content) return;

        const usedIds = new Set();
        const headingNodes = Array.from(this.content.querySelectorAll('h2, h3')).filter(
          (el) => el.textContent.trim().length > 0
        );

        headingNodes.forEach((heading) => {
          const headingText = heading.textContent.replace(/\s+/g, ' ').trim();
          if (!heading.id) {
            let id = slugifyHeading(headingText);
            if (!id) return;
            let uniqueId = id;
            let i = 2;
            while (usedIds.has(uniqueId) || document.getElementById(uniqueId)) {
              uniqueId = `${id}-${i}`;
              i += 1;
            }
            heading.id = uniqueId;
          }
          usedIds.add(heading.id);
          heading.classList.add('gl-article__heading');

          if (!heading.querySelector('.gl-article__heading-link')) {
            const textWrap = document.createElement('span');
            while (heading.firstChild) textWrap.appendChild(heading.firstChild);

            const anchor = document.createElement('a');
            anchor.href = `#${heading.id}`;
            anchor.className = 'gl-article__heading-link';
            anchor.setAttribute('aria-label', `Link to ${headingText}`);
            anchor.innerHTML =
              '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';

            heading.appendChild(textWrap);
            heading.appendChild(anchor);
            this.bindAnchor(anchor, heading.id);
          }

          this.headings.push({
            id: heading.id,
            text: headingText,
            level: Number(heading.tagName.replace('H', '')),
            el: heading,
          });
        });

        this.renderToc();
      }

      renderToc() {
        if (!this.toc || !this.tocNav) return;

        if (this.headings.length === 0) {
          this.toc.hidden = true;
          return;
        }

        this.toc.hidden = false;
        this.tocNav.replaceChildren();
        this.headings.forEach((heading) => {
          const link = document.createElement('a');
          link.href = `#${heading.id}`;
          link.className = `gl-article__toc-link${heading.level === 3 ? ' gl-article__toc-link--sub' : ''}`;
          link.dataset.tocId = heading.id;
          link.textContent = heading.text;
          this.bindAnchor(link, heading.id);
          this.tocNav.appendChild(link);
        });
        this.syncTocOpen();
      }

      bindAnchor(link, id) {
        link.addEventListener('click', (event) => {
          const target = document.getElementById(id);
          if (!target) return;
          event.preventDefault();
          target.scrollIntoView({ block: 'start' });
          history.replaceState(null, '', `#${id}`);
        });
      }

      syncTocOpen() {
        if (!this.toc || this.toc.hidden) return;
        if (this.desktopQuery.matches) this.toc.open = true;
      }

      initTocObserver() {
        if (!this.headings.length || !this.tocNav) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActiveHeading(entry.target.id);
            });
          },
          { rootMargin: '-100px 0% -70% 0%' }
        );

        this.headings.forEach((heading) => this.observer.observe(heading.el));
      }

      setActiveHeading(id) {
        this.tocNav.querySelectorAll('.gl-article__toc-link').forEach((link) => {
          const active = link.dataset.tocId === id;
          link.classList.toggle('is-active', active);
          if (active) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
      }

      initMedia() {
        if (!this.content) return;

        this.content.querySelectorAll('iframe').forEach((iframe) => {
          if (iframe.closest('.gl-article__video')) return;
          const wrapper = document.createElement('div');
          wrapper.className = 'gl-article__video gl-article__video--embed';
          iframe.parentNode.insertBefore(wrapper, iframe);
          wrapper.appendChild(iframe);
        });

        this.content.querySelectorAll('video').forEach((video) => {
          if (video.parentElement?.classList.contains('gl-article__video')) return;
          video.setAttribute('playsinline', '');
          video.controls = true;
          const wrapper = document.createElement('figure');
          wrapper.className = 'gl-article__video';
          video.parentNode.insertBefore(wrapper, video);
          wrapper.appendChild(video);
        });

        this.content.querySelectorAll('table').forEach((table) => {
          if (table.parentElement?.classList.contains('gl-article__table-wrap')) return;
          const wrapper = document.createElement('div');
          wrapper.className = 'gl-article__table-wrap';
          table.parentNode.insertBefore(wrapper, table);
          wrapper.appendChild(table);
        });
      }

      initShare() {
        const shareRoots = this.querySelectorAll('[data-article-share]');
        if (!shareRoots.length) return;

        const sourceFor = (platform) => {
          if (platform === 'x') return 'twitter';
          if (platform === 'native') return 'copy_link';
          return platform;
        };

        const trackedUrl = (shareRoot, platform) => {
          const base = shareRoot.dataset.shareUrl || `${window.location.origin}${window.location.pathname}`;
          const url = new URL(base, window.location.origin);
          ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'].forEach((param) => {
            url.searchParams.delete(param);
          });
          url.searchParams.set('utm_source', sourceFor(platform));
          url.searchParams.set('utm_medium', 'social');
          url.searchParams.set('utm_campaign', 'article_share');
          url.searchParams.set(
            'utm_content',
            [shareRoot.dataset.shareContent, shareRoot.dataset.sharePlacement].filter(Boolean).join('_')
          );
          return url.toString();
        };

        shareRoots.forEach((shareRoot) => {
          const title = shareRoot.dataset.shareTitle || document.title;
          const copiedLabel = shareRoot.dataset.copiedLabel || 'Copied!';

          shareRoot.querySelectorAll('[data-share]').forEach((button) => {
            button.addEventListener('click', () => {
              const platform = button.dataset.share;
              const url = trackedUrl(shareRoot, platform);
              const encodedUrl = encodeURIComponent(url);
              const encodedTitle = encodeURIComponent(title);
              const shareLinks = {
                x: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
                facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
                reddit: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
              };

              if (platform === 'native') {
                const showCopied = () => {
                  button.classList.add('is-copied');
                  const tooltip = button.querySelector('[data-copied]');
                  if (tooltip) tooltip.textContent = copiedLabel;
                  const status = shareRoot.querySelector('[data-share-status]');
                  if (status) {
                    status.hidden = false;
                    status.textContent = 'Link copied';
                  }
                  window.clearTimeout(button.copiedTimer);
                  button.copiedTimer = window.setTimeout(() => {
                    button.classList.remove('is-copied');
                    if (status) status.hidden = true;
                  }, 2500);
                };

                const copyNow = () => {
                  const field = document.createElement('textarea');
                  field.value = url;
                  field.setAttribute('readonly', '');
                  field.style.position = 'fixed';
                  field.style.top = '0';
                  field.style.left = '0';
                  field.style.opacity = '0';
                  document.body.appendChild(field);
                  field.focus();
                  field.select();
                  const copied = document.execCommand('copy');
                  field.remove();
                  return copied;
                };

                const copiedNow = copyNow();
                if (copiedNow) showCopied();

                if (navigator.clipboard && navigator.clipboard.writeText) {
                  navigator.clipboard.writeText(url).then(showCopied).catch(() => {});
                }
                return;
              }

              if (shareLinks[platform]) {
                window.open(shareLinks[platform], '_blank', 'width=600,height=400,noreferrer');
              }
            });
          });
        });
      }

      viewerImageWidth() {
        const ratio = window.devicePixelRatio || 1;
        return Math.min(2048, Math.max(1, Math.ceil(window.innerWidth * ratio)));
      }

      highResUrl(src) {
        try {
          const url = new URL(src, window.location.origin);
          ['width', 'height', 'crop'].forEach((param) => url.searchParams.delete(param));
          url.searchParams.set('width', String(this.viewerImageWidth()));
          return url.toString();
        } catch (error) {
          return src;
        }
      }

      initLightbox() {
        this.lightbox = this.querySelector('[data-article-lightbox]');
        if (!this.lightbox) return;

        this.lightboxImage = this.lightbox.querySelector('[data-lightbox-image]');
        this.lightboxCaption = this.lightbox.querySelector('[data-lightbox-caption]');
        this.lightboxPrev = this.lightbox.querySelector('[data-lightbox-prev]');
        this.lightboxNext = this.lightbox.querySelector('[data-lightbox-next]');
        this.lightboxSources = [];
        this.lightboxLoadToken = 0;

        this.lightboxStage = document.createElement('div');
        this.lightboxStage.className = 'gl-article__lightbox-stage';
        this.lightboxPreview = document.createElement('img');
        this.lightboxPreview.className = 'gl-article__lightbox-preview';
        this.lightboxPreview.alt = '';
        this.lightboxPreview.setAttribute('aria-hidden', 'true');
        this.lightboxImage.before(this.lightboxStage);
        this.lightboxStage.append(this.lightboxPreview, this.lightboxImage);

        const candidates = [
          ...this.querySelectorAll('.gl-article__hero img'),
          ...(this.content ? [...this.content.querySelectorAll('img')] : []),
        ];

        candidates.forEach((img) => {
          if (img.closest('[data-article-lightbox]')) return;
          const index = this.lightboxSources.length;
          this.lightboxSources.push(img);
          img.classList.add('gl-article__zoomable');
          img.tabIndex = 0;
          img.setAttribute('role', 'button');
          img.setAttribute('aria-label', img.alt ? `View larger image: ${img.alt}` : 'View larger image');

          const open = (event) => {
            const link = img.closest('a');
            if (link && link.getAttribute('href') && !/\.(avif|gif|heic|jpe?g|png|webp)(\?|$)/i.test(link.getAttribute('href'))) {
              return;
            }
            event.preventDefault();
            this.showLightbox(index);
          };

          img.addEventListener('click', open);
          img.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') open(event);
          });
        });

        this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.lightbox.close());
        this.lightboxPrev.addEventListener('click', () => this.stepLightbox(-1));
        this.lightboxNext.addEventListener('click', () => this.stepLightbox(1));
        this.lightbox.addEventListener('click', (event) => {
          if (event.target === this.lightbox) this.lightbox.close();
        });
        this.lightbox.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.stepLightbox(-1);
          if (event.key === 'ArrowRight') this.stepLightbox(1);
        });
      }

      preloadViewerImage(fullUrl, token) {
        const preload = new Image();
        preload.decoding = 'async';

        const reveal = () => {
          if (token !== this.lightboxLoadToken) return;
          const markReady = () => {
            if (token !== this.lightboxLoadToken) return;
            this.lightboxImage.classList.add('is-ready');
          };

          if (this.lightboxImage.getAttribute('src') === fullUrl && this.lightboxImage.complete) {
            markReady();
            return;
          }

          this.lightboxImage.addEventListener('load', markReady, { once: true });
          this.lightboxImage.src = fullUrl;
        };

        preload.addEventListener('load', () => {
          const decoded = typeof preload.decode === 'function' ? preload.decode() : Promise.resolve();
          decoded.then(reveal).catch(reveal);
        });
        preload.src = fullUrl;
      }

      showLightbox(index) {
        if (!this.lightboxSources.length) return;
        const total = this.lightboxSources.length;
        this.lightboxIndex = (index + total) % total;
        const img = this.lightboxSources[this.lightboxIndex];
        const previewUrl = img.currentSrc || img.src;
        const fullUrl = this.highResUrl(previewUrl);

        this.lightboxLoadToken += 1;
        const token = this.lightboxLoadToken;
        this.preloadViewerImage(fullUrl, token);

        const ratio = img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : 1.5;
        this.lightboxStage.style.setProperty('--lb-ratio', String(ratio));
        this.lightboxPreview.src = previewUrl;
        this.lightboxImage.classList.remove('is-ready');
        this.lightboxImage.alt = img.alt || '';

        const caption = img.closest('figure')?.querySelector('figcaption');
        const captionText = caption ? caption.textContent.replace(/\s+/g, ' ').trim() : '';
        this.lightboxCaption.hidden = !captionText;
        this.lightboxCaption.textContent = captionText;

        const multiple = total > 1;
        this.lightboxPrev.hidden = !multiple;
        this.lightboxNext.hidden = !multiple;
        if (!this.lightbox.open) this.lightbox.showModal();
      }

      stepLightbox(delta) {
        if (!this.lightbox.open) return;
        this.showLightbox(this.lightboxIndex + delta);
      }
    }
  );
}
